package com.yogaaicoach.pages.auth.firebase

import com.yogaaicoach.pages.auth.TokenDataManager
import com.yogaaicoach.user.UserInfo
import com.yogaaicoach.user.saveUserInfo
import kotlinx.coroutines.await
import kotlin.js.Promise

// Classic Kotlin/JS interop is simpler than wasmJs's JsAny/@JsFun: plain `external` declarations
// bind directly to the `window.*` globals set up in jsMain/resources/index.html, and `dynamic`
// allows arbitrary property access on the returned Firebase objects without extra wrapper declarations.

private external val firebaseAuth: dynamic
private external val googleAuthProvider: dynamic
private external val facebookAuthProvider: dynamic

private external fun signInWithPopup(auth: dynamic, provider: dynamic): Promise<dynamic>
private external fun createUserWithEmailAndPassword(auth: dynamic, email: String, password: String): Promise<dynamic>
private external fun firebaseSendEmailVerification(user: dynamic): Promise<dynamic>
private external fun firebaseSignInWithEmailAndPassword(auth: dynamic, email: String, password: String): Promise<dynamic>
private external fun firebaseSendPasswordResetEmail(auth: dynamic, email: String): Promise<dynamic>

// Facebook login via Facebook's own SDK + signInWithCredential (no cross-origin redirect). Returns a UserCredential.
private external fun signInWithFacebookSdk(): Promise<dynamic>

private fun processAuthResult(result: dynamic): UserInfo {
    val idToken = result._tokenResponse.idToken.toString()
    val accessToken = result.user.accessToken.toString()
    val expirationTime = result.user.stsTokenManager.expirationTime.toString().toLong()
    val refreshToken = result.user.stsTokenManager.refreshToken.toString()

    val tokenData = TokenDataManager.initialize(accessToken, refreshToken, idToken, expirationTime)
    val uid = result.user.uid.toString()
    val userInfo = UserInfo(uid, tokenData)
    saveUserInfo(uid, tokenData)
    return userInfo
}

private suspend fun signInWithProvider(provider: dynamic): UserInfo? {
    return try {
        processAuthResult(signInWithPopup(firebaseAuth, provider).await())
    } catch (e: Throwable) {
        console.error("Error during sign-in: ", e)
        null
    }
}

actual suspend fun createAccountPlatform(email: String, password: String): Result<Unit> {
    return try {
        val result = createUserWithEmailAndPassword(firebaseAuth, email, password).await()
        try {
            firebaseSendEmailVerification(result.user).await()
        } catch (e: Throwable) {
            // Some SDK versions reject this promise even when the email is sent; ignore (matches wasmJs behavior).
        }
        Result.success(Unit)
    } catch (e: Throwable) {
        Result.failure(e)
    }
}

actual suspend fun resendEmailVerificationPlatform() {
    val user = firebaseAuth.currentUser
    if (user != null) {
        try {
            firebaseSendEmailVerification(user).await()
        } catch (e: Throwable) {
            // ignore, see createAccountPlatform
        }
    }
}

actual suspend fun sendPasswordResetEmailPlatform(email: String): Result<Unit> {
    return try {
        firebaseSendPasswordResetEmail(firebaseAuth, email).await()
        Result.success(Unit)
    } catch (e: Throwable) {
        Result.failure(e)
    }
}

actual suspend fun platformSignInWithGoogle(): UserInfo? = signInWithProvider(googleAuthProvider)

// Firebase's own Facebook popup/redirect relies on cross-origin (firebaseapp.com) storage that mobile browsers
// partition away. On mobile we instead get the token from Facebook's own SDK and exchange it via
// signInWithCredential, which has no redirect and no cross-origin storage. Desktop keeps the popup UX.
actual suspend fun platformSignInWithFacebook(): UserInfo? {
    if (!isMobileBrowser()) return signInWithProvider(facebookAuthProvider)
    return try {
        processAuthResult(signInWithFacebookSdk().await())
    } catch (e: Throwable) {
        console.error("Error during Facebook sign-in: ", e)
        null
    }
}

private fun isMobileBrowser(): Boolean {
    val userAgent = kotlinx.browser.window.navigator.userAgent
    return Regex("Mobi|Android|iPhone|iPad|iPod", RegexOption.IGNORE_CASE).containsMatchIn(userAgent)
}

actual suspend fun platformSignInWithEmailPassword(email: String, password: String): Result<UserInfo> {
    return try {
        val result = firebaseSignInWithEmailAndPassword(firebaseAuth, email, password).await()
        val isEmailVerified = result.user.emailVerified == true
        if (!isEmailVerified) {
            return Result.failure(AuthError.UserNotActivated())
        }
        Result.success(processAuthResult(result))
    } catch (e: Throwable) {
        Result.failure(e)
    }
}
